import type { TFunction } from 'i18next';
import { isValidOrgNumber } from './orgNumber';
import { isValidVatSE, normalizeVat, vatFromOrgNumber, vatMatchesOrg, VAT_ORG_MISMATCH_WARNING, VAT_SE_ERROR } from './vat';

/**
 * Organisation number and VAT number of a contractor, shared by the contractor
 * onboarding and the admin's "Skapa hantverkare" so both validate the same way.
 */

/** What the organisation number field accepts while typing: digits and '-'. */
export const sanitizeOrgNumberInput = (value: string): string => value.replace(/[^\d-]/g, '');

/** What the VAT field accepts while typing: no spaces, upper case. */
export const sanitizeVatInput = (value: string): string => normalizeVat(value);

/**
 * The VAT number after the organisation number changed: derived from the
 * organisation number while the VAT field is empty, otherwise unchanged (it
 * stays editable).
 */
export const prefillVatFromOrgNumber = (currentVat: string | null | undefined, organizationNumber: string): string =>
  currentVat || vatFromOrgNumber(organizationNumber);

/** Keep an automatically derived VAT number in sync when generating again,
 * while preserving a manually entered, different VAT number. */
export function withTestOrgNumber<T extends {
  organizationNumber?: string | null;
  vatNumber?: string | null;
  notVatRegistered?: boolean;
}>(previous: T, organizationNumber: string): T {
  const deriveVat = !previous.notVatRegistered && (!previous.vatNumber ||
    vatMatchesOrg(previous.vatNumber, previous.organizationNumber || ''));
  return {
    ...previous,
    organizationNumber,
    ...(deriveVat ? { vatNumber: vatFromOrgNumber(organizationNumber) } : {})
  };
}

export interface CompanyIdentifierMessages {
  organizationNumberError?: string;
  vatNumberError?: string;
  vatNumberWarning?: string;
}

/**
 * The inline error and warning texts for the two fields. An empty field has no
 * message (required fields are checked separately). Pass `vatNumber: ''` when
 * VAT does not apply (e.g. not VAT registered).
 */
export function companyIdentifierMessages(
  t: TFunction,
  { organizationNumber, vatNumber, organizationNumberTaken }: { organizationNumber: string; vatNumber: string; organizationNumberTaken: boolean }
): CompanyIdentifierMessages {
  const organizationNumberError =
    organizationNumber && !isValidOrgNumber(organizationNumber)
      ? t('onboarding.company.organizationNumberInvalid')
      : organizationNumberTaken
        ? t('onboarding.company.organizationNumberTaken')
        : undefined;
  const vatNumberError =
    vatNumber && !isValidVatSE(vatNumber) ? t('onboarding.company.vatNumberInvalid', VAT_SE_ERROR) : undefined;
  const vatNumberWarning =
    !vatNumberError && vatNumber && !vatMatchesOrg(vatNumber, organizationNumber)
      ? t('onboarding.company.vatOrgMismatch', VAT_ORG_MISMATCH_WARNING)
      : undefined;
  return { organizationNumberError, vatNumberError, vatNumberWarning };
}
